import { useEffect, useRef, useState } from "react";

import type { RundbogenErgebnis, StichbogenErgebnis } from "@repo/bogen-calc";

import { useKalkulation } from "../../lib/kalk";
import { Feder, Geschwindigkeitsmesser, gummiband, projiziere } from "../../lib/spring";
import { ZahlFeld } from "./Feld";
import { Zahl } from "./Zahl";
import { aussenBogenPfad, bogenPfad, radius, stichVon } from "./bogenGeometrie";

const RAND = 18;

/**
 * Der Scheitel des Bogens lässt sich greifen und ziehen: der Stich folgt dem
 * Zeiger 1:1, jenseits der Grenzen mit Gummiband, und schwingt beim Loslassen
 * mit der Fingergeschwindigkeit zurück.
 */
function useScheitelGriff(svg: React.RefObject<SVGSVGElement>, stich: number, setStich: (h: number) => void, min: number, max: number) {
  const feder = useRef<Feder | null>(null);
  const messer = useRef(new Geschwindigkeitsmesser());
  const [aktiv, setAktiv] = useState(false);
  const start = useRef({ zeigerY: 0, stich: 0 });
  if (!feder.current) feder.current = new Feder(stich, (w) => setStich(Math.round(w * 10) / 10), { response: 0.35, damping: 0.8 });

  function cmProPixel(): number {
    const el = svg.current;
    if (!el) return 1;
    const ctm = el.getScreenCTM();
    return ctm ? 1 / ctm.d : 1;
  }

  return {
    aktiv,
    onPointerDown(e: React.PointerEvent) {
      feder.current?.stop();
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
      start.current = { zeigerY: e.clientY, stich };
      messer.current.reset();
      setAktiv(true);
    },
    onPointerMove(e: React.PointerEvent) {
      if (!aktiv) return;
      const roh = start.current.stich - (e.clientY - start.current.zeigerY) * cmProPixel();
      let h = roh;
      if (roh > max) h = max + gummiband(roh - max, max);
      if (roh < min) h = min + gummiband(roh - min, max);
      messer.current.erfasse(h);
      feder.current?.setze(h);
    },
    onPointerUp() {
      if (!aktiv) return;
      setAktiv(false);
      const v = messer.current.wert();
      const jetzt = feder.current?.wert ?? stich;
      const ziel = Math.min(max, Math.max(min, jetzt + projiziere(v, 0.99)));
      feder.current?.zu(ziel, { geschwindigkeit: v });
    },
  };
}

function Masslinie({ x1, y1, x2, y2, text, versatz = 0, schrift }: { x1: number; y1: number; x2: number; y2: number; text: string; versatz?: number; schrift: number }) {
  const senkrecht = x1 === x2;
  return (
    <g className="kalk-mass">
      <line x1={x1} y1={y1} x2={x2} y2={y2} />
      <text x={senkrecht ? x1 + schrift * 0.5 : (x1 + x2) / 2} y={senkrecht ? (y1 + y2) / 2 : y1 + versatz * (schrift / 4)} textAnchor={senkrecht ? "start" : "middle"} dominantBaseline="middle" style={{ fontSize: schrift, strokeWidth: schrift * 0.4 }}>
        {text}
      </text>
    </g>
  );
}

export function StichbogenRechner() {
  const [sehne, setSehne] = useState(120);
  const [stich, setStich] = useState(25);
  const svg = useRef<SVGSVGElement>(null);
  const griff = useScheitelGriff(svg, stich, setStich, 1, sehne);
  const { ergebnis, fehler, offline } = useKalkulation<StichbogenErgebnis>("stichbogen", { sehneCm: sehne, stichCm: stich }, sehne > 0 && stich > 0);

  const r = radius(sehne, Math.max(0.1, stich));
  const tiefe = stich > sehne / 2 ? r * 2 : stich;
  const schrift = Math.max(sehne, tiefe) / 22;
  const rand = RAND * (schrift / 5);
  const viewBox = `${-sehne / 2 - rand} ${-tiefe - rand} ${sehne + rand * 2 + schrift * 7} ${tiefe + rand * 2 + schrift * 2.5}`;

  return (
    <div className="kalk-rechner">
      <div className="kalk-rechner__eingabe">
        <ZahlFeld label="Sehne S" wert={sehne} onChange={setSehne} einheit="cm" min={1} />
        <ZahlFeld label="Stich h" wert={stich} onChange={setStich} einheit="cm" min={0.1} schritt={0.5} />
        <p className="kalk-hinweis">Den Scheitel im Bild greifen und ziehen, um den Stich zu ändern.</p>
      </div>
      <figure className="kalk-zeichnung">
        <svg ref={svg} viewBox={viewBox} role="img" aria-label={`Stichbogen mit Sehne ${sehne} cm und Stich ${stich} cm`}>
          <path d={bogenPfad(sehne, Math.max(0.1, stich))} className="kalk-bogen" />
          <line x1={-sehne / 2} y1={0} x2={sehne / 2} y2={0} className="kalk-sehne" />
          <line x1={0} y1={0} x2={0} y2={-stich} className="kalk-stich" />
          <Masslinie x1={-sehne / 2} y1={schrift * 1.4} x2={sehne / 2} y2={schrift * 1.4} text={`S = ${sehne} cm`} versatz={4} schrift={schrift} />
          <Masslinie x1={sehne / 2 + schrift} y1={0} x2={sehne / 2 + schrift} y2={-stich} text={`h = ${stich.toLocaleString("de-DE")} cm`} schrift={schrift} />
          <circle cx={0} cy={-stich} r={aktiv(griff.aktiv) * (schrift / 5)} className={`kalk-griff${griff.aktiv ? " kalk-griff--aktiv" : ""}`} onPointerDown={griff.onPointerDown} onPointerMove={griff.onPointerMove} onPointerUp={griff.onPointerUp} onPointerCancel={griff.onPointerUp} />
        </svg>
      </figure>
      <Ergebnisse fehler={fehler} offline={offline} warnungen={ergebnis?.warnungen}>
        {ergebnis && (
          <>
            <Kennzahl label="Radius R"><Zahl wert={ergebnis.radiusCm} art="cm" /></Kennzahl>
            <Kennzahl label="Bogenlänge"><Zahl wert={ergebnis.bogenlaengeCm} art="cm" /></Kennzahl>
            <Kennzahl label="Winkel"><Zahl wert={ergebnis.winkelGrad} art="grad" /></Kennzahl>
          </>
        )}
      </Ergebnisse>
    </div>
  );
}

function aktiv(a: boolean): number {
  return a ? 5 : 4;
}

export function RundbogenRechner() {
  const [messsehne, setMesssehne] = useState(100);
  const [stich, setStich] = useState(20);
  const [gesamt, setGesamt] = useState(120);
  const [abstand, setAbstand] = useState(5);
  const svg = useRef<SVGSVGElement>(null);
  const r1 = radius(messsehne, Math.max(0.1, stich));
  const griff = useScheitelGriff(svg, stich, setStich, 1, messsehne / 2);
  const eingabe = { messsehneCm: messsehne, stichCm: stich, gesamtsehneCm: gesamt, abstandCm: abstand };
  const { ergebnis, fehler, offline } = useKalkulation<RundbogenErgebnis>("rundbogen", eingabe, messsehne > 0 && stich > 0 && gesamt > 0);

  const zeichenbar = gesamt <= 2 * r1;
  const hg = zeichenbar ? stichVon(gesamt, r1) : stich;
  const hoehe = hg + abstand + 6;
  const breite = Math.max(gesamt, messsehne) + abstand * 2;
  const schrift = Math.max(breite, hoehe) / 22;
  const rand = RAND * (schrift / 5);
  const viewBox = `${-breite / 2 - rand} ${-hoehe - rand} ${breite + rand * 2 + schrift * 7} ${hoehe + rand * 2 + schrift * 2.5}`;
  const ht = stichVon(messsehne, r1);

  return (
    <div className="kalk-rechner">
      <div className="kalk-rechner__eingabe">
        <ZahlFeld label="Messsehne S(t)" wert={messsehne} onChange={setMesssehne} einheit="cm" min={1} />
        <ZahlFeld label="Stich h über S(t)" wert={stich} onChange={setStich} einheit="cm" min={0.1} schritt={0.5} />
        <ZahlFeld label="Gesamtsehne S(g)" wert={gesamt} onChange={setGesamt} einheit="cm" min={1} />
        <ZahlFeld label="Abstand a zur Leibung" wert={abstand} onChange={setAbstand} einheit="cm" min={0} />
      </div>
      <figure className="kalk-zeichnung">
        <svg ref={svg} viewBox={viewBox} role="img" aria-label="Rundbogenschiene">
          {zeichenbar && (
            <>
              <path d={bogenPfad(gesamt, hg)} className="kalk-bogen" />
              {abstand > 0 && <path d={aussenBogenPfad(gesamt, hg, abstand)} className="kalk-schiene" />}
              <line x1={-gesamt / 2} y1={0} x2={gesamt / 2} y2={0} className="kalk-sehne" />
              <line x1={-messsehne / 2} y1={-(hg - ht)} x2={messsehne / 2} y2={-(hg - ht)} className="kalk-sehne kalk-sehne--mess" />
              <line x1={0} y1={-(hg - ht)} x2={0} y2={-hg} className="kalk-stich" />
              <Masslinie x1={-gesamt / 2} y1={schrift * 1.4} x2={gesamt / 2} y2={schrift * 1.4} text={`S(g) = ${gesamt} cm`} versatz={4} schrift={schrift} />
              <Masslinie x1={-messsehne / 2} y1={-(hg - ht) + schrift * 0.9} x2={messsehne / 2} y2={-(hg - ht) + schrift * 0.9} text={`S(t) = ${messsehne} cm`} versatz={3.5} schrift={schrift} />
              <Masslinie x1={gesamt / 2 + schrift} y1={-(hg - ht)} x2={gesamt / 2 + schrift} y2={-hg} text={`h = ${stich.toLocaleString("de-DE")} cm`} schrift={schrift} />
              <circle cx={0} cy={-hg} r={aktiv(griff.aktiv) * (schrift / 5)} className={`kalk-griff${griff.aktiv ? " kalk-griff--aktiv" : ""}`} onPointerDown={griff.onPointerDown} onPointerMove={griff.onPointerMove} onPointerUp={griff.onPointerUp} onPointerCancel={griff.onPointerUp} />
            </>
          )}
          {!zeichenbar && (
            <text x={0} y={-hoehe / 2} textAnchor="middle" className="kalk-zeichnung__hinweis" style={{ fontSize: schrift }}>S(g) ist größer als der Durchmesser</text>
          )}
        </svg>
      </figure>
      <Ergebnisse fehler={fehler} offline={offline} warnungen={ergebnis?.warnungen}>
        {ergebnis && (
          <>
            <Kennzahl label="Dekolänge b" betont><Zahl wert={ergebnis.dekolaengeCm} art="cm" /></Kennzahl>
            <Kennzahl label="Radius Schiene"><Zahl wert={ergebnis.schienenradiusCm} art="cm" /></Kennzahl>
            <Kennzahl label="Radius Leibung"><Zahl wert={ergebnis.leibungsradiusCm} art="cm" /></Kennzahl>
            <Kennzahl label="Winkel α"><Zahl wert={ergebnis.winkelGrad} art="grad" /></Kennzahl>
          </>
        )}
      </Ergebnisse>
    </div>
  );
}

export function Kennzahl({ label, betont, children }: { label: string; betont?: boolean; children: React.ReactNode }) {
  return (
    <div className={`kalk-kennzahl${betont ? " kalk-kennzahl--betont" : ""}`}>
      <span>{label}</span>
      <strong>{children}</strong>
    </div>
  );
}

export function Ergebnisse({ fehler, offline, warnungen, children }: { fehler: string | null; offline: boolean; warnungen?: string[]; children: React.ReactNode }) {
  return (
    <div className="kalk-ergebnisse">
      {offline && <OfflineHinweis />}
      <div className={`kalk-kennzahlen${fehler ? " kalk-kennzahlen--veraltet" : ""}`}>{children}</div>
      {fehler && <p className="kalk-fehler" role="alert">{fehler}</p>}
      {warnungen?.map((w) => (
        <p key={w} className="kalk-warnung">{w}</p>
      ))}
    </div>
  );
}

export function OfflineHinweis() {
  const [sichtbar, setSichtbar] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSichtbar(true), 300);
    return () => clearTimeout(t);
  }, []);
  if (!sichtbar) return null;
  return (
    <div className="callout kalk-offline">
      <strong>Kalkulations-API nicht erreichbar.</strong>
      <p>Starten mit <code>pnpm --filter @repo/kalkulation dev</code> – der Vite-Proxy leitet <code>/kalk</code> an Port 3100 weiter.</p>
    </div>
  );
}
